import React, { useState } from 'react'

const API = process.env.REACT_APP_API_URL || 'https://sunshoos-v2.onrender.com'

const fmt = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`

const DISALLOWABLES = ['Entertaining', 'Depreciation', 'Donations', 'Bad debts']

// UK CT rates FY2023+
function computeCT(profit) {
  if (profit <= 0) return { rate: 0, tax: 0, label: 'No tax due', marginal_relief: 0 }
  if (profit <= 50000) return { rate: 19, tax: Math.round(profit * 0.19 * 100) / 100, label: 'Small profits rate (19%)', marginal_relief: 0 }
  if (profit <= 250000) {
    const mainTax = profit * 0.25
    const mr = (250000 - profit) * (profit / 250000) * (0.25 - 0.19)
    const tax = Math.round((mainTax - mr) * 100) / 100
    const effectiveRate = Math.round((tax / profit) * 10000) / 100
    return { rate: effectiveRate, tax, label: 'Marginal relief applied', marginal_relief: Math.round(mr * 100) / 100 }
  }
  return { rate: 25, tax: Math.round(profit * 0.25 * 100) / 100, label: 'Main rate (25%)', marginal_relief: 0 }
}

export default function CT600() {
  const [tab, setTab] = useState('pl')   // 'pl' | 'manual'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [saved, setSaved] = useState(false)

  // Shared fields
  const [clientName, setClientName] = useState('')
  const [companyNo, setCompanyNo] = useState('')
  const [period, setPeriod] = useState('')
  const [accountingPeriodEnd, setAccountingPeriodEnd] = useState('')

  // P&L import tab
  const [plJson, setPlJson] = useState('')

  // Manual tab
  const [manual, setManual] = useState({
    trading_profit: '',
    entertaining: '',
    depreciation: '',
    donations: '',
    other_disallowables: '',
    capital_allowances_aia: '',
    capital_allowances_pool: '',
    losses_bf: '',
    other_deductions: '',
  })

  const setM = (k, v) => setManual(m => ({ ...m, [k]: v }))

  // ── Submit P&L import ───────────────────────────────────────────────────
  async function handlePLSubmit() {
    setLoading(true); setError(''); setResult(null); setSaved(false)
    try {
      let plData = {}
      try { plData = JSON.parse(plJson) } catch { throw new Error('Invalid JSON — paste the pl_totals object from Bank Stmt → P&L') }
      const res = await fetch(`${API}/api/ct600/compute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientName || 'Client',
          company_number: companyNo,
          period: period || 'FY2024',
          accounting_period_end: accountingPeriodEnd,
          source: 'pl_import',
          pl_totals: plData,
          losses_bf: 0,
          other_deductions: 0,
          capital_allowances_aia: 0,
          capital_allowances_pool: 0,
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) { setError(e.message) }
    setLoading(false)
  }

  // ── Submit manual ───────────────────────────────────────────────────────
  async function handleManualSubmit() {
    setLoading(true); setError(''); setResult(null); setSaved(false)
    try {
      const n = (k) => parseFloat(manual[k]) || 0
      const res = await fetch(`${API}/api/ct600/compute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientName || 'Client',
          company_number: companyNo,
          period: period || 'FY2024',
          accounting_period_end: accountingPeriodEnd,
          source: 'manual',
          trading_profit: n('trading_profit'),
          disallowables: {
            entertaining: n('entertaining'),
            depreciation: n('depreciation'),
            donations: n('donations'),
            other: n('other_disallowables'),
          },
          capital_allowances_aia: n('capital_allowances_aia'),
          capital_allowances_pool: n('capital_allowances_pool'),
          losses_bf: n('losses_bf'),
          other_deductions: n('other_deductions'),
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Server error')
      setResult(data)
    } catch (e) { setError(e.message) }
    setLoading(false)
  }

  // ── Excel export ────────────────────────────────────────────────────────
  async function handleExcel() {
    try {
      const res = await fetch(`${API}/api/ct600/export-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `CT600_${(result.client_name||'Client').replace(/ /g,'_')}_${(result.period||'').replace(/ /g,'_')}.xlsx`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { setError('Excel error: ' + e.message) }
  }

  // ── PDF export ──────────────────────────────────────────────────────────
  async function handlePDF() {
    try {
      const res = await fetch(`${API}/api/ct600/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('PDF export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `CT600_${(result.client_name||'Client').replace(/ /g,'_')}_${(result.period||'').replace(/ /g,'_')}.pdf`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { setError('PDF error: ' + e.message) }
  }

  // ── Save to Supabase ────────────────────────────────────────────────────
  async function handleSave() {
    try {
      const res = await fetch(`${API}/api/ct600/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(result)
      })
      if (!res.ok) throw new Error('Save failed')
      setSaved(true)
    } catch (e) { setError('Save error: ' + e.message) }
  }

  // ── Styles (matching Sunshoos dark theme) ───────────────────────────────
  const s = {
    page: { padding: '24px', maxWidth: 960, margin: '0 auto', fontFamily: 'Calibri, sans-serif' },
    card: { background: '#0f1923', border: '1px solid rgba(201,151,58,0.15)', borderRadius: 10, padding: 24, marginBottom: 20 },
    row: { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
    label: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
    input: { background: '#0B1929', border: '1px solid rgba(201,151,58,0.2)', borderRadius: 6, color: '#fff', padding: '9px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
    tabBar: { display: 'flex', gap: 2, marginBottom: 20, background: '#0B1929', borderRadius: 8, padding: 4 },
    tab: (a) => ({ flex: 1, padding: '9px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: a ? '#C9973A' : 'transparent', color: a ? '#0B1929' : 'rgba(255,255,255,0.4)', transition: 'all .2s' }),
    btn: (v='primary') => ({ padding: '11px 22px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
      background: v==='primary'?'#C9973A':v==='green'?'#1a4a2e':v==='blue'?'#0f2a4a':v==='save'?'#1a2a4a':'#1a1a2e',
      color: v==='primary'?'#0B1929':v==='green'?'#4ade80':v==='blue'?'#93c5fd':v==='save'?'#C9973A':'#fff' }),
    sectionTitle: { fontSize: 11, fontWeight: 700, color: 'rgba(201,151,58,0.7)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 },
    computRow: (highlight) => ({
      display: 'flex', alignItems: 'center', padding: '11px 16px', borderRadius: 7, marginBottom: 5,
      background: highlight==='gold'?'rgba(201,151,58,0.1)':highlight==='green'?'rgba(74,222,128,0.08)':highlight==='red'?'rgba(239,68,68,0.08)':'rgba(255,255,255,0.03)',
      border: `1px solid ${highlight==='gold'?'rgba(201,151,58,0.25)':highlight==='green'?'rgba(74,222,128,0.2)':highlight==='red'?'rgba(239,68,68,0.2)':'rgba(255,255,255,0.06)'}`
    }),
    boxLabel: { width: 70, fontSize: 12, fontWeight: 700, color: '#C9973A', flexShrink: 0 },
    boxDesc: { flex: 1, fontSize: 12, color: 'rgba(255,255,255,0.55)' },
    boxAmt: (h) => ({ fontSize: 14, fontWeight: 700, minWidth: 130, textAlign: 'right', color: h==='green'?'#4ade80':h==='red'?'#f87171':h==='gold'?'#C9973A':'#fff' }),
  }

  const boxes = result?.boxes || {}

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>CT600 Corporation Tax</h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: '4px 0 0' }}>
          UK rates · Small profits 19% · Marginal relief · Main rate 25% · Excel + PDF output
        </p>
      </div>

      {/* Client details */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Company Details</div>
        <div style={s.row}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label style={s.label}>Client / Company Name</label>
            <input style={s.input} value={clientName} onChange={e => setClientName(e.target.value)} placeholder="e.g. Acme Trading Ltd" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={s.label}>Company Number</label>
            <input style={s.input} value={companyNo} onChange={e => setCompanyNo(e.target.value)} placeholder="e.g. 12345678" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <label style={s.label}>Accounting Period</label>
            <input style={s.input} value={period} onChange={e => setPeriod(e.target.value)} placeholder="e.g. FY2024" />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={s.label}>Period End Date</label>
            <input style={s.input} type="date" value={accountingPeriodEnd} onChange={e => setAccountingPeriodEnd(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={s.tabBar}>
        <button style={s.tab(tab==='pl')} onClick={() => setTab('pl')}>📊 Import from P&amp;L</button>
        <button style={s.tab(tab==='manual')} onClick={() => setTab('manual')}>✏️ Manual Entry</button>
      </div>

      {/* P&L import tab */}
      {tab === 'pl' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Import P&amp;L Totals</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
            Run Bank Stmt → P&amp;L first, then copy the <code style={{ color: '#C9973A' }}>pl_totals</code> JSON object from the browser console or API response and paste it below.
            The CT600 engine will auto-detect disallowables (entertaining, depreciation, donations) and compute adjusted profit.
          </p>
          <label style={s.label}>Paste pl_totals JSON</label>
          <textarea
            style={{ ...s.input, height: 140, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
            value={plJson}
            onChange={e => setPlJson(e.target.value)}
            placeholder={'{\n  "Sales/Income": 185000,\n  "Entertaining": 2400,\n  "Depreciation": 3800,\n  ...\n}'}
          />
          <div style={{ marginTop: 16 }}>
            <button style={s.btn('primary')} onClick={handlePLSubmit} disabled={loading}>
              {loading ? '⏳ Computing…' : '▶ Compute CT600 from P&L'}
            </button>
          </div>
        </div>
      )}

      {/* Manual entry tab */}
      {tab === 'manual' && (
        <div style={s.card}>
          <div style={s.sectionTitle}>Manual Entry</div>
          <div style={s.row}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={s.label}>Net Trading Profit (£)</label>
              <input style={s.input} type="number" value={manual.trading_profit} onChange={e => setM('trading_profit', e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div style={{ ...s.sectionTitle, marginTop: 8 }}>Disallowable Expenses (add back)</div>
          <div style={s.row}>
            {[['entertaining','Entertaining (£)'],['depreciation','Depreciation (£)'],['donations','Donations (£)'],['other_disallowables','Other Disallowables (£)']].map(([k,lbl]) => (
              <div key={k} style={{ flex: 1, minWidth: 150 }}>
                <label style={s.label}>{lbl}</label>
                <input style={s.input} type="number" value={manual[k]} onChange={e => setM(k, e.target.value)} placeholder="0.00" />
              </div>
            ))}
          </div>

          <div style={{ ...s.sectionTitle, marginTop: 8 }}>Capital Allowances (deduct)</div>
          <div style={s.row}>
            {[['capital_allowances_aia','AIA (100%) (£)'],['capital_allowances_pool','Main Pool 18% (£)']].map(([k,lbl]) => (
              <div key={k} style={{ flex: 1, minWidth: 180 }}>
                <label style={s.label}>{lbl}</label>
                <input style={s.input} type="number" value={manual[k]} onChange={e => setM(k, e.target.value)} placeholder="0.00" />
              </div>
            ))}
          </div>

          <div style={{ ...s.sectionTitle, marginTop: 8 }}>Reliefs &amp; Losses</div>
          <div style={s.row}>
            {[['losses_bf','Losses B/F (£)'],['other_deductions','Other Deductions (£)']].map(([k,lbl]) => (
              <div key={k} style={{ flex: 1, minWidth: 180 }}>
                <label style={s.label}>{lbl}</label>
                <input style={s.input} type="number" value={manual[k]} onChange={e => setM(k, e.target.value)} placeholder="0.00" />
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <button style={s.btn('primary')} onClick={handleManualSubmit} disabled={loading}>
              {loading ? '⏳ Computing…' : '▶ Compute CT600'}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background:'rgba(220,38,38,0.08)', border:'1px solid rgba(220,38,38,0.25)', borderRadius:8, padding:'12px 16px', marginBottom:16, color:'#f87171', fontSize:13 }}>
          ⚠ {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div style={s.card}>
          {/* Result header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
            <div>
              <div style={{ fontSize:17, fontWeight:700, color:'#fff' }}>{result.client_name} — {result.period}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.35)', marginTop:3 }}>
                Co. {result.company_number || '—'} · Period end: {result.accounting_period_end || '—'} · {result.source === 'pl_import' ? 'Imported from P&L' : 'Manual entry'}
              </div>
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button style={s.btn('green')} onClick={handleExcel}>⬇ Excel</button>
              <button style={s.btn('blue')} onClick={handlePDF}>⬇ PDF</button>
              <button style={s.btn('save')} onClick={handleSave} disabled={saved}>
                {saved ? '✓ Saved' : '💾 Save to Client'}
              </button>
            </div>
          </div>

          {/* CT600 Computation boxes */}
          <div style={s.sectionTitle}>Tax Computation</div>

          {[
            { key:'box155', label:'Box 155', desc:'Net profit per accounts',               highlight:'' },
            { key:'box160', label:'Box 160', desc:'Disallowable expenses added back',       highlight:'' },
            { key:'box165', label:'Box 165', desc:'Capital allowances deducted',            highlight:'' },
            { key:'box170', label:'Box 170', desc:'Losses brought forward',                 highlight:'' },
            { key:'box175', label:'Box 175', desc:'Other deductions',                       highlight:'' },
            { key:'box235', label:'Box 235', desc:'Profits chargeable to Corporation Tax',  highlight:'gold' },
            { key:'box330', label:'Box 330', desc:'FY tax rate applied',                    highlight:'', isPct: true },
            { key:'box340', label:'Box 340', desc:'Marginal relief',                        highlight:'' },
            { key:'box345', label:'Box 345', desc:'Corporation Tax chargeable',             highlight:'gold' },
            { key:'box430', label:'Box 430', desc:'Tax payable',                            highlight:'green' },
          ].map(({ key, label, desc, highlight, isPct }) => (
            <div key={key} style={s.computRow(highlight)}>
              <div style={s.boxLabel}>{label}</div>
              <div style={s.boxDesc}>{desc}</div>
              <div style={s.boxAmt(highlight)}>
                {isPct ? fmtPct(boxes[key]) : fmt(boxes[key])}
              </div>
            </div>
          ))}

          {/* Tax payable callout */}
          <div style={{ marginTop:18, padding:'16px 20px', borderRadius:9,
            background: boxes.box430 > 0 ? 'rgba(201,151,58,0.08)' : 'rgba(74,222,128,0.08)',
            border: `1px solid ${boxes.box430 > 0 ? 'rgba(201,151,58,0.3)' : 'rgba(74,222,128,0.3)'}` }}>
            <div style={{ fontSize:13, fontWeight:700, color: boxes.box430 > 0 ? '#C9973A' : '#4ade80' }}>
              {boxes.box430 > 0 ? '→ Corporation Tax Due to HMRC' : '✓ No Corporation Tax Due'}
            </div>
            <div style={{ fontSize:28, fontWeight:800, color:'#fff', marginTop:4 }}>{fmt(boxes.box430)}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{result.rate_label}</div>
          </div>

          {/* Disallowables breakdown */}
          {result.disallowables_breakdown && Object.keys(result.disallowables_breakdown).length > 0 && (
            <div style={{ marginTop:18 }}>
              <div style={s.sectionTitle}>Disallowables Added Back</div>
              {Object.entries(result.disallowables_breakdown).map(([k, v]) => v > 0 && (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'7px 14px', borderRadius:6, marginBottom:4, background:'rgba(255,255,255,0.03)', fontSize:12 }}>
                  <span style={{ color:'rgba(255,255,255,0.55)', textTransform:'capitalize' }}>{k.replace(/_/g,' ')}</span>
                  <span style={{ color:'#f87171', fontWeight:600 }}>{fmt(v)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Flags */}
          {result.flags && result.flags.length > 0 && (
            <div style={{ marginTop:16 }}>
              <div style={s.sectionTitle}>Review Flags</div>
              {result.flags.map((flag, i) => (
                <div key={i} style={{ display:'flex', gap:8, padding:'9px 12px', background:'rgba(255,170,0,0.06)', borderRadius:6, marginBottom:5, border:'1px solid rgba(255,170,0,0.15)' }}>
                  <span style={{ color:'#FBBF24' }}>⚠</span>
                  <span style={{ fontSize:12, color:'rgba(255,255,255,0.65)' }}>{flag}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
